(ns manage.adset-preview-panel.util.context
  (:require [camel-snake-kebab.core :refer [->kebab-case]]
            [clojure.string :as str]
            [com.rpl.specter :as specter]
            [common.content-function.core :refer [MACROS]]
            [common.content-function.schemas :as schema-util]
            [common.util :as util :refer [leaf-paths pull]]
            [manage.adset-preview-panel.util.predicate :as cfn-predicate]))

(defn find-schema
  "Find matching schema by given selector as keyword.

   Example:

   ```clojure
   (def schema-urns [{:alias \"device\"
                      :schema \"urn:device\"}])
   (def context-schemas [{:meta {:id \"urn:device\"}}])

   (find-schema schema-urns context-schemas :device)

   ;; => {:meta {:id \"urn:device\"}}
   ```"
  [schema-urns context-schemas k]
  (let [urn (schema-util/find-urn (name k) schema-urns)]
    (schema-util/find-schema urn context-schemas)))

(defn field-property-type
  "Returns a string as property type where field is selected
   by given selector and schema. When multiple types are found they
   are imploded with `:`.

   The default type is `\"string\"` when type can not be found."
  [selector schema]
  (let [->coll (comp (partial remove nil?)
                     #(if (coll? %) % (vector %)))
        types (->coll (schema-util/find-type selector schema))]
    (if (seq types)
      (str/join ":" types)
      "string")))

(defn field-property
  "Get field property.

   Example:

   ```clojure
   (def schema {:data {:out {:touch
                              {:type \"bool\"
                               :operand-label \"Supported\"}}}})
   (field-property [:touch] schema)

   ;; => {:type \"bool\"
   ;;     :values [{:label \"Is supported\" :value true}
   ;;              {:label \"Is not supported\" :value false}]}
   ```"
  [selector schema _alias context]
  (let [type (field-property-type selector schema)
        {:keys [operand-label minimum maximum]
         :or {operand-label "true"}} (schema-util/find-property selector schema)
        default-props {:type type}]
    (merge (case type
             "bool" (let [label-fn #(str % " " (-> operand-label
                                                   str/lower-case))
                          values (->> {true "Is" false "Is not"}
                                      (map (fn [[k v]]
                                             {:label (label-fn v) :value k})))]
                      {:values values})
             "enum" {:values (schema-util/find-values selector schema (:param context))}
             "set:option" {:values (schema-util/find-values selector schema (:param context))}
             "integer" {:minimum minimum :maximum maximum}
             "option" {:values (schema-util/find-values selector schema (:param context))}
             {})
           default-props)))

(defn context-type->fields
  "Transform context type into fields.

   Example:

   ```clojure
   (context-type->fields schema
     '(:device {:isPhone nil :os \"Android\"}))

   ;; => '({:type :device
   ;;       :sub-type :isPhone
   ;;       :name \"Device - Call capabilities\"
   ;;       :value nil
   ;;       :field {:type \"bool\"
   ;;               :values [{:label \"Is supported\" :value true}
   ;;                        {:label \"Is not supported\" :value false}]}}
   ;;      {:type :device
   ;;       :sub-type :os
   ;;       :name \"Device - Operating system\" :value \"Android\"})
   ;;       :value nil
   ;;       :field {:type \"bool\"
   ;;               :values [{:label \"Is supported\" :value true}
   ;;                        {:label \"Is not supported\" :value false}]}})
   ````"
  [schema [k v] context]
  (let [capitalize-key (comp str/capitalize name)
        labels->name #(->> % (remove nil?) (str/join " - "))
        type-label (get-in schema [:meta :label])]
    (map (fn [[value-k value-v]]
           (let [selector [(->kebab-case value-k)]
                 property (schema-util/find-property selector schema)
                 default-labels [(capitalize-key k)
                                 (capitalize-key value-k)]
                 name* (labels->name [type-label
                                      (:label property)])]
             {:type k
              :sub-type value-k
              :name (if (empty? name*)
                      (labels->name default-labels)
                      name*)
              :value value-v
              :field (field-property selector schema k context)})) v)))

(defn- context->fields*
  "Transform context into a list of fields.
   This function makes it easy to render a component within the view for
   the given context.
   Example:
   ```clojure
   (def my-context {:device {:touch true}})
   (context->fields* schema my-context)
   ;; => '({:type :device
   ;;       :sub-type :touch
   ;;       :name \"Device - Touch\"
   ;;       :value true
   ;;       :field {:type \"bool\"
   ;;               :values [{:label \"Is supported\" :value true}
   ;;                        {:label \"Is not supported\" :value false}]}})
   ```"
  [schema-urns context-schemas context]
  (let [find-schema* (partial find-schema schema-urns context-schemas)]
    (->> (mapcat (fn [[k v]]
                   (let [key-context (some #(and (= (:alias %) (name k)) %) schema-urns)]
                     (context-type->fields (find-schema* k) [k v] key-context)))
                 context)
         (map (fn [{:keys [type] :as field}]
                (cond
                  (str/starts-with? (name type) "products-") (-> field
                                                                 (assoc :name (str (str/capitalize (name type)) " - Product Count"))
                                                                 (update :value #(when (nat-int? %) %)))
                  (= (:name field) "Dynamic feed") (assoc field :name (str (name (:type field)) " - " (name (:sub-type field))))
                  (= (:sub-type field) :split) (assoc field :name (str "Split Testing - " (name type)))
                  :else field))))))

(def context->fields (memoize context->fields*))

(defn context-with-schema-properties
  "Convert context values from x into `{:value x}`.
   Schema properties of context type are appended to this map:
   `{:value x :type y ...}`

   Example:

   ```clojure
   (def context {:datetime {:timestamp {:timestamp \"2020-04-15T12:12:21.911823+02:00\"}
                            :day-of-month 15}})

   (def schema-urns [{:schema \"urn:lemonpi:schema:context:datetime:v1\"
                      :alias \"datetime\"}])

   (def context-schemas [{:meta {:alias \"datetime\"
                                :id \"urn:lemonpi:schema:context:datetime:v1\"}
                         :data {:out {:timestamp {:type \"timestamp\"}
                                      :day-of-month {:type \"integer\"}}}}])

   (context-with-schema-properties schema-urns
                                   context-schemas
                                   context)

   ;;=> {:datetime {:timestamp {:value {:timestamp \"2020-04-15T12:12:21.911823+02:00\"}
   ;;                           :type \"timestamp\"
   ;;                           :label \"Exact date/time\"}
   ;;               :day-of-month: {:value \"15\"
   ;;                               :type \"integer\"
   ;;                               :minimum 1
   ;;                               :maximum 31
   ;;                               :label \"Monthly recurring\"}}}
   ```"
  [schema-urns context-schemas context]
  (let [->schema (partial find-schema schema-urns context-schemas)
        ->value (fn [schema [k v]]
                  (let [properties (schema-util/find-property [k] schema)]
                    (hash-map k (merge properties
                                       {:value v}))))]
    (reduce-kv
     (fn [m k v]
       (assoc m k (->> v
                       (map (partial ->value (->schema k)))
                       (into {}))))
     {} context)))

(defn context->unparse-values
  "Map all context values with `cfn-predicate/unparse-value`

   Example:

   ```clojure
   (def context {:datetime {:timestamp {:timestamp \"2020-04-15T12:12:21.911823+02:00\"}}})

   (context->unparse-values context)
   ;; => {:datetime {:timestamp \"2020-04-15T12:12:21.911823+02:00\"}}
   ```"
  [schema-urns context-schemas context]
  (letfn [(transformv [[k {:keys [type value]}]]
            (vector k (cfn-predicate/unparse-value {:macro-type type} value)))]
    (specter/transform [specter/MAP-VALS specter/ALL] transformv
                       (context-with-schema-properties schema-urns
                                                       context-schemas
                                                       context))))

(defn fields->context
  "Tranform fields into context.
  This function enables the conversion of edited fields into context.

  Example:

  ```clojure
  (def fields '({:type :device
                :sub-type :os
                :value \"Android\"}))

  (fields->context fields)

  ;; => {:device {:os \"Android\"}}
  ```"
  [fields]
  (reduce (fn [acc {:keys [type sub-type value field]}]
            (let [macro-type (-> field :type name)
                  parsed-value (cfn-predicate/parse-value
                                {:macro-type macro-type} value)]
              (assoc-in acc [type sub-type] parsed-value)))
          {} fields))

(defn update-fields-value
  "Update the value of fields by given predicate and value.

  Example:

  ```clojure
  (def fields '({:type :device
                :sub-type :os
                :name \"Device - Operating system\"
                :value \"Android\"
                :field {:type \"enum\"
                        :values [{:value \"Android\" :label \"Android\"}
                                 {:value \"Windows\" :label \"Windows\"}]}}))

  (def pred (comp #(= :device %) :type))

  (update-fields-value fields pred \"Windows\")

  ;; => '({:type :device
          :sub-type :os
          :name \"Device - Operating system\"
          :value \"Windows\"
          :field {:type \"enum\"
                  :values [{:value \"Android\" :label \"Android\"}
                           {:value \"Windows\" :label \"Windows\"}]}})
  ```"
  [fields pred value]
  (specter/transform [specter/ALL pred] #(assoc % :value value) fields))

(defn cfn->context
  "Populate context map from content function by extracting macros.
   All values will be set to nil. This function is useful for DCO flow
   where the context macros are populated from the content function.

   Example:

   ```clojure
   (def cfn {:data {:rules [{:assignments [{:expr \"${device.isPhone}\"
                                            :name \"phone\"}]}]}})

   (cfn->context cfn)
   ;; => {:device {:isPhone nil}}
   ```

   By default indexed based macros are excluded by default.
   You can control the output by using a predicate as second argument,
   for example:

   ```clojure
   (def cfn {:data :rules [{:assignments [{:expr \"${device.os}\"
                                           :name \"operating_system\"}
                                          {:expr \"${instagram.influencer}\"
                                           :name \"instagram\"}]}]})

   (def pred #(= (:alias %) :device))

   (cfn->context cfn pred)

   ;; => {:device {:os nil}}
   ;; note: instagram is excluded by the predicate
   ```"
  ([cfn] (cfn->context cfn #(= (count (:selector %)) 1)))
  ([cfn pred]
   (let [output (specter/select MACROS (:data cfn))
         selector->m (fn [selector]
                       (->> selector
                            (map #(hash-map % nil))
                            (into {})))
         reduce-fn (fn [acc {:keys [alias selector] :as m}]
                     (if (pred m)
                       (assoc acc alias (-> (get acc alias {})
                                            (merge (selector->m selector))))
                       acc))]
     (reduce reduce-fn {} output))))

(defn overwrite-context-values
  "Copies values from source context to target context.
   NOTE:
   - no nesting is introduced or removed from target
   - only non-nil values from src are used

   Example:

   ```clojure
   (def context-a {:device {:isPhone true}})
   (def context-b {:device {:isPhone nil}
                   :instagram {:influencer true}})

   (overwrite-context-values context-a context-b)
   ;; => {:device {:isPhone true}
                  {:instagram {:influencer true}}}

   ;; note: the value of `:isPhone` is adopted
   ```"
  [src-ctx target-ctx]
  (let [nil-or-map? (some-fn nil? map?)
        ->merge (fn [a b]
                  (if (map? a) (merge a b) b))]
    (merge-with ->merge target-ctx
                (pull src-ctx (leaf-paths target-ctx) {:remove-vals nil-or-map?}))))
